import React, { useState } from 'react';
import { Home, Recycle, Leaf, Snowflake, Trees, ChevronRight, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { services } from '../mock';

const iconMap = {
  home: Home,
  recycle: Recycle,
  leaf: Leaf,
  snowflake: Snowflake,
  trees: Trees
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName];
    return Icon ? <Icon className="w-8 h-8" /> : null;
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Unsere Dienstleistungen
          </h2>
          <p className="text-xl text-gray-600">
            Umfassende Lösungen für Ihre Immobilie – von der Hausbetreuung bis zur
            professionellen Entsorgung
          </p>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card
              key={service.id}
              className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#2563EB]/50 cursor-pointer overflow-hidden"
              style={{
                animationDelay: `${index * 0.1}s`,
                animation: 'fadeInUp 0.6s ease-out forwards',
                opacity: 0
              }}
              onClick={() => setSelectedService(service)}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#2563EB] shadow-lg">
                  {getIcon(service.icon)}
                </div>
              </div>

              <CardHeader>
                <CardTitle className="text-2xl text-gray-900 group-hover:text-[#2563EB] transition-colors">
                  {service.title}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  {service.description}
                </CardDescription>
              </CardHeader>

              <CardContent>
                <ul className="space-y-2 mb-4">
                  {service.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#2563EB] mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant="ghost"
                  className="w-full text-[#2563EB] hover:text-[#6BA72F] hover:bg-[#2563EB]/10 group/btn"
                >
                  Mehr erfahren
                  <ChevronRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Special callout for Hausbetreuung services */}
        <div className="mt-16 bg-gradient-to-br from-[#2563EB]/10 to-[#1E40AF]/10 rounded-3xl p-8 lg:p-12 border border-[#2563EB]/20">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Hausbetreuung – Spezialdienste
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {services[0].subServices.map((subService, index) => (
              <Card
                key={subService.id}
                className="hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-[#2563EB]/50"
                onClick={() => setSelectedService(subService)}
              >
                <div className="relative h-40 overflow-hidden rounded-t-lg">
                  <img
                    src={subService.image}
                    alt={subService.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 w-10 h-10 bg-white rounded-lg flex items-center justify-center text-[#1E40AF] shadow-lg">
                    {getIcon(subService.icon)}
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{subService.title}</CardTitle>
                  <CardDescription>{subService.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Service detail modal */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedService && (
            <>
              <DialogHeader>
                <div className="mb-4 h-64 rounded-xl overflow-hidden">
                  <img
                    src={selectedService.image}
                    alt={selectedService.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <DialogTitle className="text-3xl text-[#1E40AF]">
                  {selectedService.title}
                </DialogTitle>
                <DialogDescription className="text-lg">
                  {selectedService.description}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-6">
                <h4 className="font-semibold text-lg text-gray-900">Leistungen im Detail:</h4>
                <ul className="space-y-3">
                  {selectedService.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => {
                    setSelectedService(null);
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full mt-6 bg-[#2563EB] hover:bg-[#6BA72F] text-white text-lg py-6"
                >
                  Angebot für {selectedService.title} anfordern
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Services;
